import React from 'react';
import { FileUp, Users, Plug, Shield, Building2 } from 'lucide-react';

interface ApproachBadgeProps {
  approach: string;
  platform?: string;
  size?: 'sm' | 'md' | 'lg';
}

const ApproachBadge: React.FC<ApproachBadgeProps> = ({ approach, platform, size = 'md' }) => {
  const getApproachDetails = (approach: string) => {
    const details: { [key: string]: { label: string; icon: any; bgColor: string; textColor: string; borderColor: string; description: string } } = {
      manual_trigger: {
        label: 'Manual Trigger',
        icon: FileUp,
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-300',
        description: 'Quick win - User uploads file to trigger automation'
      },
      delegated_user_auth: {
        label: 'User Authentication',
        icon: Users,
        bgColor: 'bg-slate-50',
        textColor: 'text-slate-700',
        borderColor: 'border-slate-300',
        description: 'Uses individual user credentials - no service account needed'
      },
      premium_connectors: {
        label: 'Premium Connectors',
        icon: Plug,
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-700',
        borderColor: 'border-purple-300',
        description: 'Pre-built connectors with premium license'
      },
      service_account: {
        label: 'Service Account',
        icon: Shield,
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300',
        description: 'Dedicated automation account for unattended workflows'
      },
      enterprise_integration: {
        label: 'Enterprise Integration',
        icon: Building2,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-400',
        description: 'Full enterprise architecture involvement'
      }
    };

    return details[approach] || {
      label: approach,
      icon: Shield,
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
      description: 'Standard integration approach'
    };
  };

  const details = getApproachDetails(approach);
  const Icon = details.icon;

  const sizeClasses = {
    sm: {
      container: 'px-3 py-1.5 text-xs gap-1.5',
      icon: 'w-3 h-3',
      text: 'text-xs'
    },
    md: {
      container: 'px-4 py-2 text-sm gap-2',
      icon: 'w-4 h-4',
      text: 'text-sm'
    },
    lg: {
      container: 'px-6 py-3 text-base gap-3',
      icon: 'w-5 h-5',
      text: 'text-base'
    }
  };

  const classes = sizeClasses[size];

  return (
    <div className="inline-block">
      <div className={`${details.bgColor} ${details.textColor} border ${details.borderColor} rounded-lg ${classes.container} flex items-center font-semibold`}>
        <Icon className={classes.icon} />
        <span>{details.label}</span>
      </div>
      {size !== 'sm' && (
        <p className="text-xs text-gray-500 mt-1">{details.description}</p>
      )}
      {platform && size === 'lg' && (
        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
          <Plug className="w-3 h-3" />
          <span>Platform: {platform}</span>
        </div>
      )}
    </div>
  );
};

export default ApproachBadge;
